// src/pages/Admin/Blogs/BlogForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../db/firebase';
import { ArrowLeft, Save, Upload, Image, Eye, X, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';

// Image Upload and Editor Component
const ImageUploader = ({ onImageInsert, onClose }) => {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 400, height: 'auto' });
  const [alignment, setAlignment] = useState('center');
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!imageFile) return;

    try {
      setUploading(true);
      const storageRef = ref(storage, `blog-content-images/${Date.now()}_${imageFile.name}`);
      const snapshot = await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Create image markdown with styling
      const imageMarkdown = `![Image](${downloadURL})`;
      
      onImageInsert(imageMarkdown);
      onClose();
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Insert Image</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Upload Area */}
          {!imagePreview && (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => document.getElementById('image-upload').click()}
            >
              <Upload size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-lg text-gray-600 mb-2">Click to upload or drag and drop</p>
              <p className="text-sm text-gray-500">PNG, JPG, GIF up to 10MB</p>
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
              />
            </div>
          )}

          {/* Image Preview and Editing Tools */}
          {imagePreview && (
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="text-center mb-4">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="mx-auto max-h-64 object-contain rounded"
                    style={{
                      transform: `rotate(${rotation}deg) scale(${scale})`,
                      maxWidth: `${imageSize.width}px`,
                      height: imageSize.height === 'auto' ? 'auto' : `${imageSize.height}px`
                    }}
                  />
                </div>

                {/* Editing Controls */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Size Controls */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Width (px)
                    </label>
                    <input
                      type="range"
                      min="100"
                      max="800"
                      value={imageSize.width}
                      onChange={(e) => setImageSize(prev => ({ ...prev, width: parseInt(e.target.value) }))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>100px</span>
                      <span>{imageSize.width}px</span>
                      <span>800px</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Height
                    </label>
                    <select
                      value={imageSize.height}
                      onChange={(e) => setImageSize(prev => ({ ...prev, height: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="auto">Auto (Maintain aspect ratio)</option>
                      <option value="200">200px</option>
                      <option value="300">300px</option>
                      <option value="400">400px</option>
                      <option value="500">500px</option>
                    </select>
                  </div>

                  {/* Transform Controls */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rotation
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setRotation(prev => prev - 90)}
                        className="p-2 border rounded hover:bg-gray-100"
                      >
                        <RotateCw size={16} className="transform rotate-180" />
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="360"
                        value={rotation}
                        onChange={(e) => setRotation(parseInt(e.target.value))}
                        className="flex-1"
                      />
                      <button
                        onClick={() => setRotation(prev => prev + 90)}
                        className="p-2 border rounded hover:bg-gray-100"
                      >
                        <RotateCw size={16} />
                      </button>
                      <span className="text-sm w-12 text-center">{rotation}°</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Scale
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setScale(prev => Math.max(0.5, prev - 0.1))}
                        className="p-2 border rounded hover:bg-gray-100"
                      >
                        <ZoomOut size={16} />
                      </button>
                      <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={scale}
                        onChange={(e) => setScale(parseFloat(e.target.value))}
                        className="flex-1"
                      />
                      <button
                        onClick={() => setScale(prev => Math.min(2, prev + 0.1))}
                        className="p-2 border rounded hover:bg-gray-100"
                      >
                        <ZoomIn size={16} />
                      </button>
                      <span className="text-sm w-12 text-center">{scale.toFixed(1)}x</span>
                    </div>
                  </div>

                  {/* Alignment */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alignment
                    </label>
                    <div className="flex gap-2">
                      {['left', 'center', 'right'].map((align) => (
                        <button
                          key={align}
                          onClick={() => setAlignment(align)}
                          className={`flex-1 py-2 border rounded capitalize ${
                            alignment === align
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {align}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview('');
                    setImageSize({ width: 400, height: 'auto' });
                    setAlignment('center');
                    setRotation(0);
                    setScale(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Change Image
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploading ? 'Uploading...' : 'Insert Image'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder = "Start writing your blog post..." }) => {
  const [showImageUploader, setShowImageUploader] = useState(false);

  const handleFormat = (format) => {
    const textarea = document.getElementById('blog-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    let formattedText = '';
    
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
      case 'h1':
        formattedText = `# ${selectedText}`;
        break;
      case 'h2':
        formattedText = `## ${selectedText}`;
        break;
      case 'h3':
        formattedText = `### ${selectedText}`;
        break;
      case 'bullet':
        formattedText = `- ${selectedText}`;
        break;
      case 'number':
        formattedText = `1. ${selectedText}`;
        break;
      case 'link':
        const url = prompt('Enter URL:');
        if (url) formattedText = `[${selectedText}](${url})`;
        else formattedText = selectedText;
        break;
      case 'image':
        setShowImageUploader(true);
        return; // Don't insert anything, let the image uploader handle it
      case 'quote':
        formattedText = `> ${selectedText}`;
        break;
      case 'code':
        formattedText = `\`\`\`\n${selectedText}\n\`\`\``;
        break;
      default:
        formattedText = selectedText;
    }
    
    const newValue = value.substring(0, start) + formattedText + value.substring(end);
    onChange(newValue);
    
    // Restore focus and selection
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedText.length);
    }, 0);
  };

  const handleImageInsert = (imageMarkdown) => {
    const textarea = document.getElementById('blog-editor');
    const start = textarea.selectionStart;
    const newValue = value.substring(0, start) + '\n' + imageMarkdown + '\n' + value.substring(start);
    onChange(newValue);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + imageMarkdown.length + 2, start + imageMarkdown.length + 2);
    }, 0);
  };

  const ToolbarButton = ({ onClick, children, title, active = false }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`px-3 py-2 border rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
        active
          ? 'bg-purple-100 text-purple-700 border-purple-300'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* Enhanced Toolbar */}
      <div className="border-b border-gray-300 bg-gray-50 p-3 flex flex-wrap gap-2">
        {/* Text Formatting */}
        <div className="flex gap-1">
          <ToolbarButton onClick={() => handleFormat('h1')} title="Heading 1">
            <span className="font-bold">H1</span>
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('h2')} title="Heading 2">
            <span className="font-bold">H2</span>
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('h3')} title="Heading 3">
            <span className="font-bold">H3</span>
          </ToolbarButton>
        </div>
        
        {/* Text Styles */}
        <div className="flex gap-1">
          <ToolbarButton onClick={() => handleFormat('bold')} title="Bold">
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('italic')} title="Italic">
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('underline')} title="Underline">
            <u>U</u>
          </ToolbarButton>
        </div>
        
        {/* Lists */}
        <div className="flex gap-1">
          <ToolbarButton onClick={() => handleFormat('bullet')} title="Bullet List">
            • List
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('number')} title="Numbered List">
            1. List
          </ToolbarButton>
        </div>

        {/* Media */}
        <div className="flex gap-1">
          <ToolbarButton 
            onClick={() => handleFormat('image')} 
            title="Insert Image"
            active={showImageUploader}
          >
            <Image size={16} />
            Image
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('link')} title="Insert Link">
            🔗 Link
          </ToolbarButton>
        </div>

        {/* Blocks */}
        <div className="flex gap-1">
          <ToolbarButton onClick={() => handleFormat('quote')} title="Insert Quote">
            ❝ Quote
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('code')} title="Insert Code Block">
            {`</>`} Code
          </ToolbarButton>
        </div>
      </div>
      
      {/* Textarea */}
      <textarea
        id="blog-editor"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-96 p-4 resize-none focus:outline-none text-gray-900 leading-relaxed font-sans text-base"
      />

      {/* Image Uploader Modal */}
      {showImageUploader && (
        <ImageUploader
          onImageInsert={handleImageInsert}
          onClose={() => setShowImageUploader(false)}
        />
      )}
    </div>
  );
};

// Fixed Markdown Preview Component with proper image handling
const MarkdownPreview = ({ content }) => {
  const renderMarkdown = (text) => {
    if (!text) return '<p class="text-gray-500 italic">Start typing to see preview...</p>';
    
    let html = text;

    // Process images FIRST - before any other processing
    const imageRegex = /!\[(.*?)\]\((.*?)\)/g;
    html = html.replace(imageRegex, '<div class="my-6"><img src="$2" alt="$1" class="max-w-full h-auto rounded-lg shadow-md mx-auto border" loading="lazy" /></div>');

    // Now process other markdown elements
    html = html
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>')
      // Bold and italic
      .replace(/\*\*\*(.*?)\*\*\*/gim, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="font-bold">$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>')
      // Underline
      .replace(/<u>(.*?)<\/u>/gim, '<u class="underline">$1</u>')
      // Links - only match actual http/https links
      .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/gim, '<a href="$2" class="text-blue-600 hover:underline" target="_blank">$1</a>')
      // Blockquotes
      .replace(/^> (.*$)/gim, '<blockquote class="border-l-4 border-purple-500 pl-4 py-2 my-4 bg-purple-50 text-gray-700 italic">$1</blockquote>')
      // Code blocks
      .replace(/```([^`]+)```/gim, '<pre class="bg-gray-800 text-gray-100 p-4 rounded-lg my-4 overflow-x-auto"><code class="font-mono text-sm">$1</code></pre>')
      // Inline code
      .replace(/`([^`]+)`/gim, '<code class="bg-gray-100 px-2 py-1 rounded text-sm font-mono border">$1</code>')
      // Line breaks
      .replace(/\n/g, '<br>');

    // Handle lists
    const lines = html.split('<br>');
    let inList = false;
    let listType = '';
    
    const processedLines = lines.map(line => {
      // Skip processing if line contains images (already processed)
      if (line.includes('<img') || line.includes('class="my-6"')) {
        if (inList) {
          inList = false;
          return `</${listType}>${line}`;
        }
        return line;
      }
      
      // Bullet lists
      if (line.trim().startsWith('- ')) {
        if (!inList || listType !== 'ul') {
          inList = true;
          listType = 'ul';
          return `<ul class="list-disc ml-6 my-3 space-y-1"><li>${line.trim().substring(2)}</li>`;
        }
        return `<li>${line.trim().substring(2)}</li>`;
      }
      
      // Numbered lists
      if (/^\d+\. .*$/.test(line.trim())) {
        if (!inList || listType !== 'ol') {
          inList = true;
          listType = 'ol';
          return `<ol class="list-decimal ml-6 my-3 space-y-1"><li>${line.trim().replace(/^\d+\.\s*/, '')}</li>`;
        }
        return `<li>${line.trim().replace(/^\d+\.\s*/, '')}</li>`;
      }
      
      // Close list if we were in one
      if (inList && line.trim() === '') {
        inList = false;
        return `</${listType}><br>`;
      }
      
      if (inList) {
        inList = false;
        return `</${listType}>${line}`;
      }
      
      return line;
    });

    html = processedLines.join('<br>');
    
    // Close any open list
    if (inList) {
      html += `</${listType}>`;
    }

    // Wrap in paragraph if no block elements
    const hasBlockElements = html.includes('<h') || html.includes('<ul') || html.includes('<ol') || 
                            html.includes('<blockquote') || html.includes('<img') || html.includes('<pre') ||
                            html.includes('<div class="my-6">');
    
    if (!hasBlockElements) {
      html = html.split('<br><br>').join('</p><p class="my-3 leading-relaxed">');
      html = `<p class="my-3 leading-relaxed">${html}</p>`;
    }

    return html;
  };

  return (
    <div 
      className="min-h-96 p-4 border border-gray-300 rounded-lg bg-white prose prose-lg max-w-none"
      dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
    />
  );
};

const BlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [form, setForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    author: 'Admin',
    status: 'draft',
    featuredImage: '',
    tags: []
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditing);
  const [imageUploading, setImageUploading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isEditing) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      setFetchLoading(true);
      const docRef = doc(db, 'blogs', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setForm({ ...docSnap.data() });
      } else {
        alert('Blog not found!');
        navigate('/admin/blogs');
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      alert('Error loading blog');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFeaturedImageUpload = async (file) => {
    if (!file) return;
    
    try {
      setImageUploading(true);
      const storageRef = ref(storage, `blog-images/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      handleChange('featuredImage', downloadURL);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    } finally {
      setImageUploading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      handleChange('tags', [...form.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    handleChange('tags', form.tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim() || !form.content.trim()) {
      alert('Please fill in title and content');
      return;
    }

    try {
      setLoading(true);
      
      const blogData = {
        ...form,
        updatedAt: serverTimestamp()
      };

      if (isEditing) {
        // Update existing blog
        await updateDoc(doc(db, 'blogs', id), blogData);
        alert('Blog updated successfully!');
      } else {
        // Create new blog
        blogData.createdAt = serverTimestamp();
        const newDocRef = doc(collection(db, 'blogs'));
        await setDoc(newDocRef, blogData);
        alert('Blog created successfully!');
      }
      
      navigate('/admin/blogs');
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Error saving blog');
    } finally {
      setLoading(false);
    }
  };

  const markdownGuide = `# Blog Formatting Guide

## Text Formatting
- **Bold**: **text**
- *Italic*: *text*
- <u>Underline</u>: <u>text</u>

## Headers
# Header 1
## Header 2  
### Header 3

## Lists
- Bullet points: - item
- Numbered lists: 1. item

## Media & Links
- Links: [link text](https://example.com)
- Images: Use the Image button to upload and resize images
- Quotes: > This is a quote

## Code
- Inline code: \`code\`
- Code blocks: \\\`\\\`\\\` code \\\`\\\`\\\`

## Tips
- Use the image uploader to insert and resize images directly
- Add relevant tags for better organization
- Write a compelling excerpt to attract readers`;

  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
            </h1>
            <p className="text-gray-600 mt-2">
              {isEditing ? 'Update your blog post content' : 'Write and publish a new blog post'}
            </p>
          </div>
          <Link
            to="/admin/blogs"
            className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Back to Blogs
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold mb-6 text-gray-900">Basic Information</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Title *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Enter blog post title"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Author
                </label>
                <input
                  type="text"
                  value={form.author}
                  onChange={(e) => handleChange('author', e.target.value)}
                  placeholder="Author name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                />
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Excerpt
                </label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => handleChange('excerpt', e.target.value)}
                  placeholder="Brief description of your blog post that will appear in listings and search results..."
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-vertical"
                />
                <p className="text-sm text-gray-500 mt-2">
                  {form.excerpt.length}/200 characters • Keep it concise and engaging
                </p>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold mb-6 text-gray-900">Featured Image</h2>
            <div className="flex flex-col md:flex-row gap-8 items-start">
              {/* Image Preview */}
              {form.featuredImage && (
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img
                      src={form.featuredImage}
                      alt="Featured"
                      className="w-64 h-40 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleChange('featuredImage', '')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Upload Area */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Upload Featured Image
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center transition-colors hover:border-purple-400">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFeaturedImageUpload(e.target.files[0])}
                    className="hidden"
                    id="featured-image"
                  />
                  <label
                    htmlFor="featured-image"
                    className="cursor-pointer flex flex-col items-center gap-3"
                  >
                    <Upload size={32} className="text-gray-400" />
                    <div>
                      <p className="text-lg text-gray-600 font-medium">
                        {imageUploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>
                    </div>
                  </label>
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  A featured image makes your blog post more engaging and shareable on social media.
                </p>
              </div>
            </div>
          </div>

          {/* Content Editor */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold mb-6 text-gray-900">Content *</h2>
            
            {/* Editor */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Blog Content Editor
              </label>
              <RichTextEditor
                value={form.content}
                onChange={(value) => handleChange('content', value)}
                placeholder="Start writing your amazing blog post here... Use the toolbar above to format your content, add images, links, and more."
              />
            </div>

            {/* Preview Toggle */}
            <div className="flex justify-between items-center mb-4">
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Eye size={18} />
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </button>
              
              <div className="text-sm text-gray-500">
                {form.content.length} characters • {form.content.split(/\s+/).filter(word => word.length > 0).length} words
              </div>
            </div>

            {/* Preview */}
            {showPreview && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Eye size={16} />
                  Live Preview
                </label>
                <MarkdownPreview content={form.content} />
              </div>
            )}
            
            {/* Formatting Guide */}
            <div className="mt-6 p-5 bg-blue-50 rounded-xl border border-blue-200">
              <details className="cursor-pointer">
                <summary className="font-medium text-blue-800 text-lg flex items-center gap-2">
                  📝 Blog Formatting Guide
                </summary>
                <div className="mt-4 text-blue-700">
                  <MarkdownPreview content={markdownGuide} />
                </div>
              </details>
            </div>
          </div>

          {/* Tags & Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold mb-6 text-gray-900">Tags & Settings</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Tags
                </label>
                <div className="flex gap-3 mb-4">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add a tag (e.g., technology, tutorial, news)"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap"
                  >
                    Add Tag
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 px-4 py-2 rounded-full text-sm font-medium border border-purple-200 flex items-center gap-2"
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="text-purple-600 hover:text-purple-800 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                  {form.tags.length === 0 && (
                    <p className="text-gray-500 text-sm italic">No tags added yet. Add tags to help organize and categorize your blog post.</p>
                  )}
                </div>
              </div>

              {/* Settings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Publication Status
                </label>
                <select
                  value={form.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                >
                  <option value="draft">📝 Save as Draft</option>
                  <option value="published">📢 Publish Now</option>
                </select>
                <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">
                    {form.status === 'draft' 
                      ? 'Your blog will be saved as a draft and won\'t be visible to the public.'
                      : 'Your blog will be published immediately and visible to all visitors.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="text-sm text-gray-600">
              {isEditing ? 'Update your blog post with the changes made.' : 'Ready to publish your new blog post?'}
            </div>
            <div className="flex gap-4">
              <Link
                to="/admin/blogs"
                className="bg-gray-500 text-white px-8 py-3 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={20} />
                {loading ? 'Saving...' : (isEditing ? 'Update Blog Post' : 'Publish Blog Post')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogForm;